import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { APIError, useAxios } from "../core/_axios";
import { Service } from "../core/_models";
import LoadingSpinner from "./LoadingSpinner";
import dayjs from "dayjs";
import { calculateTrendLine, generateTrendLineData, CmgrDetails } from "../utils/trendLineCalculator";
import { Download } from "lucide-react";

import weekOfYear from "dayjs/plugin/weekOfYear";   // adds .week()/.weekOfYear() + "WW" token
import isoWeek from "dayjs/plugin/isoWeek";      // makes startOf("week") Monday-based (ISO-8601)
import weekday from "dayjs/plugin/weekday";      // lets you tweak weekday(0) etc. if you need it
import dayOfYear from "dayjs/plugin/dayOfYear";  // adds .dayOfYear() method
import { getGraphOfSignaturesOverTime } from "../core/_requests_v2";

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);      // optional but handy for Monday-start calendars
dayjs.extend(weekday);      // optional
dayjs.extend(dayOfYear);    // for year projections

type GroupBy = "" | "week" | "month" | "year";

// Fill in missing periods with zeros and add projections
function fillMissingPeriods(data: { date: string; count: number }[], groupBy: GroupBy): { date: string; count: number; actualCount?: number; isProjected?: boolean; hideFromGraph?: boolean }[] {
    if (data.length === 0) return data;

    const result: { date: string; count: number; actualCount?: number; isProjected?: boolean; hideFromGraph?: boolean }[] = [];

    if (groupBy === "month") {
        const monthMap = new Map(data.map(d => [d.date, d.count]));

        // Find the earliest and latest dates
        const dates = data.map(d => d.date).sort();
        const startDate = dayjs(dates[0]);
        const currentMonth = dayjs().format('YYYY-MM');
        const endDate = dayjs(currentMonth);

        // Generate all months from start to current
        let current = startDate;
        while (current.isBefore(endDate) || current.format('YYYY-MM') === currentMonth) {
            const monthKey = current.format('YYYY-MM');
            const count = monthMap.get(monthKey) || 0;

            if (monthKey === currentMonth && count > 0) {
                const dayOfMonth = dayjs().date();

                // Hide current month from graph if less than 7 days into the month
                if (dayOfMonth < 7) {
                    // Still calculate projection for visualization when enabled
                    const daysInMonth = dayjs().daysInMonth();
                    const projectedCount = Math.round((count / dayOfMonth) * daysInMonth);

                    result.push({
                        date: monthKey,
                        count: projectedCount > count ? projectedCount : count,
                        actualCount: count,
                        isProjected: projectedCount > count,
                        hideFromGraph: true
                    });
                } else {
                    // For current month, show projected value with actual as metadata
                    const daysInMonth = dayjs().daysInMonth();
                    const projectedCount = Math.round((count / dayOfMonth) * daysInMonth);

                    if (projectedCount > count) {
                        result.push({
                            date: monthKey,
                            count: projectedCount,
                            actualCount: count,
                            isProjected: true
                        });
                    } else {
                        result.push({ date: monthKey, count });
                    }
                }
            } else if (monthKey === currentMonth && count === 0) {
                // Current month with no data yet — skip entirely
            } else {
                result.push({ date: monthKey, count });
            }

            current = current.add(1, 'month');
        }
    } else if (groupBy === "year") {
        const yearMap = new Map(data.map(d => [d.date, d.count]));
        
        // Find the earliest and latest years
        const years = data.map(d => d.date).sort();
        const startYear = years[0];
        const currentYear = dayjs().format('YYYY');
        
        // Generate all years from start to current
        for (let year = parseInt(startYear); year <= parseInt(currentYear); year++) {
            const yearKey = year.toString();
            const count = yearMap.get(yearKey) || 0;
            
            if (yearKey === currentYear && count > 0) {
                // For current year, show projected value with actual as metadata
                const dayOfYear = dayjs().dayOfYear();
                const daysInYear = dayjs().endOf('year').dayOfYear();
                const projectedCount = Math.round((count / dayOfYear) * daysInYear);
                
                if (projectedCount > count) {
                    result.push({ 
                        date: yearKey, 
                        count: projectedCount, 
                        actualCount: count, 
                        isProjected: true 
                    });
                } else {
                    result.push({ date: yearKey, count });
                }
            } else {
                result.push({ date: yearKey, count });
            }
        }
    } else {
        return data;
    }
    
    return result;
}

function groupData(
    data: { date: string; count: number }[],
    groupBy: GroupBy
): { date: string; count: number }[] {
    if (!groupBy) return data;

    const grouped: Record<string, number> = {};

    data.forEach(({ date, count }) => {
        let key = date;
        if (groupBy === "week") {
            key = dayjs(date).startOf("week").format("YYYY-MM-DD");   // e.g. "2025-05-19"
        } else if (groupBy === "month") {
            key = dayjs(date).format("YYYY-MM");
        } else if (groupBy === "year") {
            key = dayjs(date).format("YYYY");
        }
        grouped[key] = (grouped[key] || 0) + count;
    });

    return Object.entries(grouped)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));
}

export function SignaturesGraph({ service }: { service?: Service }) {
    const axios = useAxios();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [data, setData] = useState<{ date: string, count: number }[]>([]);
    const [preneedCount, setPreneedCount] = useState<number>(0);
    const [groupBy, setGroupBy] = useState<GroupBy>("month");
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showCurrentMonth, setShowCurrentMonth] = useState(false); // Show current month projection on graph
    const [policyValue, setPolicyValue] = useState<number>(() => {
        const stored = localStorage.getItem('signaturesGraph-policyValue');
        return stored ? Number(stored) : 5500;
    });
    const [insuranceMargin, setInsuranceMargin] = useState<number>(() => {
        const stored = localStorage.getItem('signaturesGraph-insuranceMargin');
        return stored ? Number(stored) : 25;
    });
    const [showPolicyPopover, setShowPolicyPopover] = useState(false);
    const [popoverPolicyValue, setPopoverPolicyValue] = useState<number>(policyValue);
    const [showMarginPopover, setShowMarginPopover] = useState(false);
    const [popoverMarginValue, setPopoverMarginValue] = useState<number>(insuranceMargin);
    const [showCmgrModal, setShowCmgrModal] = useState(false);
    const [cmgrIncludeCurrent, setCmgrIncludeCurrent] = useState<boolean>(() => {
        const stored = localStorage.getItem('signaturesGraph-cmgrIncludeCurrent');
        return stored === 'true';
    });
    const policyPopoverRef = useRef<HTMLDivElement>(null);
    const policyValueInputRef = useRef<HTMLInputElement>(null);
    const marginPopoverRef = useRef<HTMLDivElement>(null);

    const loadSignatures = useCallback(async () => {
        setLoadingError(null);
        setLoading(true);
        try {
            const result = await getGraphOfSignaturesOverTime(axios);
            const formatted = result.signatures.map((item) => ({
                date: item.date,
                count: item.count
            }));
            setData(formatted);
            setPreneedCount(result.preneed_count);
        } catch (exception) {
            console.error(exception);
            const errorMessage =
                exception instanceof APIError
                    ? exception.message
                    : "An unknown error has occurred. Please try again.";
            setLoadingError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [axios]);

    useEffect(() => {
        loadSignatures();
    }, [loadSignatures]);

    // Close policy popover on outside click or Escape
    useEffect(() => {
        if (!showPolicyPopover) return;
        const handleClick = (e: MouseEvent) => {
            if (policyPopoverRef.current && !policyPopoverRef.current.contains(e.target as Node)) {
                setShowPolicyPopover(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setShowPolicyPopover(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [showPolicyPopover]);

    // Close margin popover on outside click or Escape
    useEffect(() => {
        if (!showMarginPopover) return;
        const handleClick = (e: MouseEvent) => {
            if (marginPopoverRef.current && !marginPopoverRef.current.contains(e.target as Node)) {
                setShowMarginPopover(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setShowMarginPopover(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [showMarginPopover]);

    useEffect(() => {
        if (!showCmgrModal) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setShowCmgrModal(false);
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [showCmgrModal]);

    // Grouped data calculation must happen before any conditional returns
    const groupedData = useMemo(() => {
        if (!data || data.length === 0) return [];
        return fillMissingPeriods(groupData(data, groupBy), groupBy);
    }, [data, groupBy]);

    // Calculate trend line data (only for month view)
    const { trendLineData, trendInfo } = useMemo(() => {
        if (groupBy !== 'month') return { trendLineData: null, trendInfo: null };

        // When "include current month" is on, include the current month's projected
        // full-month value (extrapolated from partial data) as a real data point.
        // But only if we have enough data (7+ days) — early-month projections are too unreliable.
        let trendData = groupedData;
        if (cmgrIncludeCurrent) {
            trendData = groupedData.map(d => {
                if ((d as any).isProjected) {
                    // 7+ days into the month — reliable enough to include
                    return { ...d, isProjected: false };
                }
                // hideFromGraph means < 7 days — too unreliable, leave it excluded
                return d;
            });
        }

        const trend = calculateTrendLine(trendData as any);
        if (trend) {
            console.log('SignaturesGraph trend calculated:', {
                type: trend.type,
                dataPoints: groupedData.length,
                firstValue: groupedData[0]?.count,
                lastValue: groupedData[groupedData.length - 1]?.count,
                exponentialR2: trend.exponentialR2,
                linearR2: trend.linearR2,
                cmgr: trend.cmgr
            });

            // Only generate trend line data if the fit is good enough
            const hasMeaningfulTrend = (trend.exponentialR2 && trend.exponentialR2 > 0.7) ||
                                       (trend.linearR2 && trend.linearR2 > 0.5);

            return {
                trendLineData: hasMeaningfulTrend ? generateTrendLineData(groupedData, trend) : null,
                trendInfo: {
                    type: trend.type,
                    exponentialR2: trend.exponentialR2,
                    linearR2: trend.linearR2,
                    cmgr: trend.cmgr,
                    cmgrDetails: trend.cmgrDetails
                }
            };
        } else {
            console.log('SignaturesGraph: No trend detected for data', {
                dataPoints: groupedData.length,
                firstValue: groupedData[0]?.count,
                lastValue: groupedData[groupedData.length - 1]?.count
            });
            return { trendLineData: null, trendInfo: null };
        }
    }, [groupedData, groupBy, cmgrIncludeCurrent]);

    // Get current period data for display
    const currentPeriodData = groupedData[groupedData.length - 1];
    const hasProjection = currentPeriodData?.isProjected;

    // Calculate total signatures and value metrics - MUST be before conditional returns
    const totalSignatures = useMemo(() => {
        return groupedData.reduce((sum, d) => sum + (d.actualCount || d.count), 0);
    }, [groupedData]);

    // Insurance value calculations with conservative assumptions - MUST be before conditional returns
    const valueMetrics = useMemo(() => {
        if (!totalSignatures) return null;

        // Conservative conversion assumptions
        const HIGH_INTENT_CLOSE_RATE = 0.35; // 35% close rate for qualified leads
        const GENERAL_CONVERSION_RATE = 0.02; // 2% general conversion rate
        const marginRate = insuranceMargin / 100;

        const interestedCount = preneedCount; // actual count from database
        const remainingCount = totalSignatures - interestedCount;

        const highIntentConversions = Math.round(interestedCount * HIGH_INTENT_CLOSE_RATE);
        const generalConversions = Math.round(remainingCount * GENERAL_CONVERSION_RATE);
        const totalConversions = highIntentConversions + generalConversions;

        const totalRevenue = totalConversions * policyValue;
        const insuranceProfit = Math.round(totalRevenue * marginRate);

        return {
            totalSignatures,
            interestedCount,
            highIntentConversions,
            generalConversions,
            totalConversions,
            totalRevenue,
            insuranceProfit
        };
    }, [totalSignatures, preneedCount, policyValue, insuranceMargin]);

    // Calculate projected monthly value in 12 months based on CMGR or selected growth rate - MUST be before conditional returns
    const projectedMonthlyValue = useMemo(() => {
        if (!trendInfo || trendInfo.cmgr === undefined || !groupedData.length || groupBy !== 'month') return null;

        let currentMonthlySignatures: number;
        if (cmgrIncludeCurrent) {
            // Use current month's projected full-month value as baseline, but only if 7+ days in.
            // hideFromGraph means < 7 days — too unreliable, fall back to last completed month.
            const currentMonth = dayjs().format('YYYY-MM');
            const projectedEntry = groupedData.find((g: any) => g.isProjected);
            const exactMonthEntry = groupedData.find((g: any) => g.date === currentMonth && !g.hideFromGraph && !g.isProjected);
            const currentEntry = projectedEntry || exactMonthEntry;
            if (currentEntry) {
                currentMonthlySignatures = (currentEntry as any).count || 0;
            } else {
                // Fall back to last completed month (< 7 days into current month or no current data)
                const completed = groupedData.filter(d => !d.hideFromGraph && !d.isProjected);
                currentMonthlySignatures = completed[completed.length - 1]?.count || 0;
            }
        } else {
            const completed = groupedData.filter(d => !d.hideFromGraph && !d.isProjected);
            currentMonthlySignatures = completed[completed.length - 1]?.count || 0;
        }
        const growthRate = trendInfo.cmgr;
        const projectedSignatures = Math.round(currentMonthlySignatures * Math.pow(1 + growthRate, 12));

        const HIGH_INTENT_CLOSE_RATE = 0.35;
        const GENERAL_CONVERSION_RATE = 0.02;
        // Use actual preneed rate from database
        const actualPreneedRate = totalSignatures > 0 ? preneedCount / totalSignatures : 0;
        const interestedCount = Math.round(projectedSignatures * actualPreneedRate);
        const remainingCount = projectedSignatures - interestedCount;
        const highIntentConversions = Math.round(interestedCount * HIGH_INTENT_CLOSE_RATE);
        const generalConversions = Math.round(remainingCount * GENERAL_CONVERSION_RATE);
        const conversions = highIntentConversions + generalConversions;
        const monthlyRevenue = conversions * policyValue;

        return {
            projectedSignatures,
            conversions,
            highIntentConversions,
            generalConversions,
            monthlyRevenue,
            growthRate
        };
    }, [trendInfo, groupedData, groupBy, totalSignatures, preneedCount, policyValue, cmgrIncludeCurrent]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (loadingError) {
        return (
            <div>
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">{loadingError}</span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={() => {
                        setLoadingError(null);
                        loadSignatures();
                    }}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }
    // Export data as CSV
    const exportData = () => {
        if (!groupedData || groupedData.length === 0) return;

        // Prepare CSV content
        const csvContent = [
            'Date,Count', // Header
            ...groupedData.map(item => {
                // Use projected count for last item if it exists, otherwise use regular count
                const count = item.count;
                return `${item.date},${count}`;
            })
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        // Generate filename with current date and view type
        const timestamp = dayjs().format('YYYY-MM-DD');
        const filename = `signatures-${groupBy || 'daily'}-${timestamp}.csv`;

        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Get current period label and count for subtitle
    const getCurrentPeriodLabel = () => {
        const current = currentPeriodData;
        if (!current) return null;

        let label = "";
        let value = current.isProjected ? current.actualCount : current.count;
        let projected = current.isProjected ? current.count : null;

        if (groupBy === "") {
            label = "Today";
        } else if (groupBy === "week") {
            label = "This week";
        } else if (groupBy === "month") {
            label = "This month";
        } else if (groupBy === "year") {
            label = "This year";
        }

        if (projected) {
            return `${value} ${label.toLowerCase()} • Projected: ${projected}`;
        }
        return `${value} ${label.toLowerCase()}`;
    };

    const applyPolicyValue = () => {
        if (popoverPolicyValue > 0) {
            setPolicyValue(popoverPolicyValue);
            localStorage.setItem('signaturesGraph-policyValue', String(popoverPolicyValue));
        }
        setShowPolicyPopover(false);
    };

    const resetPolicyValue = () => {
        setPolicyValue(5500);
        setPopoverPolicyValue(5500);
        localStorage.setItem('signaturesGraph-policyValue', '5500');
        setShowPolicyPopover(false);
    };

    const applyMarginValue = () => {
        if (popoverMarginValue > 0 && popoverMarginValue <= 100) {
            setInsuranceMargin(popoverMarginValue);
            localStorage.setItem('signaturesGraph-insuranceMargin', String(popoverMarginValue));
        }
        setShowMarginPopover(false);
    };

    const resetMarginValue = () => {
        setInsuranceMargin(25);
        setPopoverMarginValue(25);
        localStorage.setItem('signaturesGraph-insuranceMargin', '25');
        setShowMarginPopover(false);
    };

    return (<>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Right column (on desktop): Value + Growth + Projections */}
            <div className="lg:col-span-1 lg:order-last flex flex-col gap-3">
                {valueMetrics && (
                    <div className="card bg-gradient-to-r from-green-50 to-emerald-50">
                        <div className="card-body py-3">
                            <div className="flex items-start justify-between mb-2">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-800 mb-0.5">Generated Value to Date</h4>
                                    <p className="text-xs text-gray-600">{totalSignatures.toLocaleString()} qualified contacts • {valueMetrics.interestedCount.toLocaleString()} high-intent leads ({(valueMetrics.interestedCount / totalSignatures * 100).toFixed(1)}%)</p>
                                </div>
                            </div>
                            <div className="text-center mb-2">
                                <div className="text-2xl font-bold text-green-700">
                                    {valueMetrics.insuranceProfit >= 1000000
                                        ? `$${(valueMetrics.insuranceProfit / 1000000).toFixed(2)}M`
                                        : `$${Math.round(valueMetrics.insuranceProfit / 1000).toLocaleString()}K`
                                    }
                                </div>
                                <div className="text-xs text-gray-600 relative inline-block">
                                    Insurance Profit (
                                    <span
                                        className="cursor-pointer border-b border-dashed border-gray-400 hover:text-gray-700"
                                        onClick={() => {
                                            setPopoverMarginValue(insuranceMargin);
                                            setShowMarginPopover(!showMarginPopover);
                                        }}
                                    >
                                        {insuranceMargin}% margin
                                    </span>
                                    )
                                    {showMarginPopover && (
                                        <div
                                            ref={marginPopoverRef}
                                            className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50 w-64"
                                        >
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Insurance Margin (%)</label>
                                            <input
                                                type="number"
                                                value={popoverMarginValue}
                                                onChange={(e) => setPopoverMarginValue(Number(e.target.value))}
                                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                                                autoFocus
                                                min={1}
                                                max={100}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') applyMarginValue();
                                                }}
                                            />
                                            <div className="flex flex-wrap gap-1.5 mb-2">
                                                {[
                                                    { label: '10%', value: 10, desc: 'Low' },
                                                    { label: '25%', value: 25, desc: 'Standard' },
                                                    { label: '35%', value: 35, desc: 'Mid' },
                                                    { label: '50%', value: 50, desc: 'High' },
                                                ].map((preset) => (
                                                    <button
                                                        key={preset.value}
                                                        onClick={() => {
                                                            setPopoverMarginValue(preset.value);
                                                            setInsuranceMargin(preset.value);
                                                            localStorage.setItem('signaturesGraph-insuranceMargin', String(preset.value));
                                                        }}
                                                        className={`px-2 py-1 rounded text-xs cursor-pointer transition-opacity ${
                                                            insuranceMargin === preset.value
                                                                ? 'bg-blue-100 text-blue-700 font-semibold ring-1 ring-blue-400'
                                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                        }`}
                                                    >
                                                        {preset.label} <span className="text-gray-400">{preset.desc}</span>
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <button
                                                    onClick={resetMarginValue}
                                                    className="text-xs text-gray-500 hover:text-gray-700"
                                                >
                                                    Reset
                                                </button>
                                                <button
                                                    onClick={applyMarginValue}
                                                    className="btn btn-sm btn-primary"
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-green-200">
                                <div className="text-center">
                                    <div className="text-lg font-semibold text-gray-800">{valueMetrics.totalConversions.toLocaleString()}</div>
                                    <div className="text-xs text-gray-600">Projected Policy Sales</div>
                                    <div className="text-xs text-gray-500 mt-0.5">{valueMetrics.highIntentConversions.toLocaleString()} high-intent • {valueMetrics.generalConversions.toLocaleString()} general</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-lg font-semibold text-gray-800">
                                        {valueMetrics.totalRevenue >= 1000000
                                            ? `$${(valueMetrics.totalRevenue / 1000000).toFixed(2)}M`
                                            : `$${Math.round(valueMetrics.totalRevenue / 1000).toLocaleString()}K`
                                        }
                                    </div>
                                    <div className="text-xs text-gray-600">Total Premium Revenue</div>
                                    <div className="text-xs text-gray-500 mt-0.5 relative inline-block">
                                        <span
                                            className="cursor-pointer border-b border-dashed border-gray-400 hover:text-gray-700"
                                            onClick={() => {
                                                setPopoverPolicyValue(policyValue);
                                                setShowPolicyPopover(!showPolicyPopover);
                                            }}
                                        >
                                            @ ${policyValue >= 1000 ? `${(policyValue / 1000).toFixed(policyValue % 1000 === 0 ? 0 : 1)}K` : policyValue.toLocaleString()} avg policy value
                                        </span>
                                        {showPolicyPopover && (
                                            <div
                                                ref={policyPopoverRef}
                                                className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-3 z-50 w-64"
                                            >
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Avg Policy Value ($)</label>
                                                <input
                                                    ref={policyValueInputRef}
                                                    type="number"
                                                    value={popoverPolicyValue}
                                                    onChange={(e) => setPopoverPolicyValue(Number(e.target.value))}
                                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                                                    autoFocus
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') applyPolicyValue();
                                                    }}
                                                />
                                                <div className="flex flex-wrap gap-1.5 mb-2">
                                                    {[
                                                        { label: '$3K', value: 3000, desc: 'Low' },
                                                        { label: '$5.5K', value: 5500, desc: 'Standard' },
                                                        { label: '$8K', value: 8000, desc: 'Mid' },
                                                        { label: '$15K', value: 15000, desc: 'High' },
                                                    ].map((preset) => (
                                                        <button
                                                            key={preset.value}
                                                            onClick={() => {
                                                                setPopoverPolicyValue(preset.value);
                                                                setPolicyValue(preset.value);
                                                                localStorage.setItem('signaturesGraph-policyValue', String(preset.value));
                                                            }}
                                                            className={`px-2 py-1 rounded text-xs cursor-pointer transition-opacity ${
                                                                policyValue === preset.value
                                                                    ? 'bg-blue-100 text-blue-700 font-semibold ring-1 ring-blue-400'
                                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                            }`}
                                                        >
                                                            {preset.label} <span className="text-gray-400">{preset.desc}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <button
                                                        onClick={resetPolicyValue}
                                                        className="text-xs text-gray-500 hover:text-gray-700"
                                                    >
                                                        Reset
                                                    </button>
                                                    <button
                                                        onClick={applyPolicyValue}
                                                        className="btn btn-sm btn-primary"
                                                    >
                                                        Apply
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Growth Performance section */}
                {groupBy === 'month' && trendInfo && trendInfo.cmgr !== undefined && (() => {
                    const currentValue = groupedData.filter(d => !d.hideFromGraph && !d.isProjected)[groupedData.filter(d => !d.hideFromGraph && !d.isProjected).length - 1]?.count || 0;
                    const cmgrPercent = trendInfo.cmgr * 100;
                    const projectedIn12Months = Math.round(currentValue * Math.pow(1 + trendInfo.cmgr, 12));

                    return (
                        <div className="card">
                            <div className="card-body py-3 text-sm">
                                <div className="font-semibold text-gray-700 mb-2">
                                    Growth Performance{' '}
                                    <span
                                        className="cursor-pointer border-b border-dashed border-green-400 text-green-700 hover:text-green-900"
                                        onClick={() => setShowCmgrModal(true)}
                                    >
                                        CMGR: {cmgrPercent.toFixed(1)}%
                                    </span>
                                </div>
                                {projectedMonthlyValue && (
                                    <div className="bg-green-50 rounded-lg p-3">
                                        <div className="font-semibold text-gray-700 mb-1">12-Month Projection</div>
                                        <p className="text-xs text-gray-600 mb-2">{projectedMonthlyValue.projectedSignatures.toLocaleString()} leads/mo → {projectedMonthlyValue.conversions.toLocaleString()} policies/mo</p>
                                        {/* Annual — hero numbers */}
                                        <div className="text-center mb-2">
                                            <div className="text-2xl font-bold text-green-800">
                                                {(() => {
                                                    const annualProfit = Math.round(projectedMonthlyValue.monthlyRevenue * 12 * (insuranceMargin / 100));
                                                    return annualProfit >= 1000000
                                                        ? `$${(annualProfit / 1000000).toFixed(2)}M`
                                                        : `$${Math.round(annualProfit / 1000).toLocaleString()}K`;
                                                })()}
                                            </div>
                                            <div className="text-xs text-gray-600">Annual Profit ({insuranceMargin}% margin)</div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-green-200">
                                            <div className="text-center">
                                                <div className="text-lg font-semibold text-gray-800">
                                                    {(projectedMonthlyValue.conversions * 12).toLocaleString()}
                                                </div>
                                                <div className="text-xs text-gray-600">Annual Policies</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-lg font-semibold text-gray-800">
                                                    {(() => {
                                                        const annualRevenue = projectedMonthlyValue.monthlyRevenue * 12;
                                                        return annualRevenue >= 1000000
                                                            ? `$${(annualRevenue / 1000000).toFixed(2)}M`
                                                            : `$${Math.round(annualRevenue / 1000).toLocaleString()}K`;
                                                    })()}
                                                </div>
                                                <div className="text-xs text-gray-600">Annual Revenue</div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-green-200">
                                            <div className="text-center">
                                                <div className="text-lg font-semibold text-gray-800">
                                                    {(() => {
                                                        const monthlyProfit = Math.round(projectedMonthlyValue.monthlyRevenue * (insuranceMargin / 100));
                                                        return monthlyProfit >= 1000000
                                                            ? `$${(monthlyProfit / 1000000).toFixed(2)}M`
                                                            : `$${Math.round(monthlyProfit / 1000).toLocaleString()}K`;
                                                    })()}
                                                </div>
                                                <div className="text-xs text-gray-600">Monthly Profit</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-lg font-semibold text-gray-800">
                                                    {projectedMonthlyValue.monthlyRevenue >= 1000000
                                                        ? `$${(projectedMonthlyValue.monthlyRevenue / 1000000).toFixed(2)}M`
                                                        : `$${Math.round(projectedMonthlyValue.monthlyRevenue / 1000).toLocaleString()}K`
                                                    }
                                                </div>
                                                <div className="text-xs text-gray-600">Monthly Revenue</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                {/* Current month note (left column) */}
                {groupBy === 'month' && groupedData.some(d => d.hideFromGraph) && !showCurrentMonth && (
                    <div className="text-xs text-gray-500 italic px-1">
                        Current month will appear on graph after {7 - dayjs().date()} more day{7 - dayjs().date() !== 1 ? 's' : ''}
                    </div>
                )}
            </div>

            {/* Left column (on desktop): Graph */}
            <div className="lg:col-span-2 lg:order-first flex flex-col">
                <div className="card flex flex-col flex-1">
                    <div className="card-header flex-wrap gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                            <h3 className="card-title whitespace-nowrap">Signatures Over Time</h3>
                            {currentPeriodData && (
                                <span className="text-sm text-gray-500 whitespace-nowrap">{getCurrentPeriodLabel()}</span>
                            )}
                        </div>
                        <div className="flex gap-1.5 flex-wrap">
                            <button
                                onClick={() => setGroupBy("")}
                                className={`btn btn-sm ${groupBy === "" ? "btn-primary" : "btn-light"}`}
                            >
                                Day
                            </button>
                            <button
                                onClick={() => setGroupBy("week")}
                                className={`btn btn-sm ${groupBy === "week" ? "btn-primary" : "btn-light"}`}
                            >
                                Week
                            </button>
                            <button
                                onClick={() => setGroupBy("month")}
                                className={`btn btn-sm ${groupBy === "month" ? "btn-primary" : "btn-light"}`}
                            >
                                Month
                            </button>
                            <button
                                onClick={() => setGroupBy("year")}
                                className={`btn btn-sm ${groupBy === "year" ? "btn-primary" : "btn-light"}`}
                            >
                                Year
                            </button>
                            {showAdvanced && (
                                <button
                                    onClick={exportData}
                                    className="btn btn-sm btn-light"
                                    title="Export data as CSV"
                                >
                                    <Download className="w-4 h-4" />
                                </button>
                            )}
                            <button
                                onClick={() => setShowAdvanced(!showAdvanced)}
                                className={`btn btn-sm ${showAdvanced ? "btn-warning" : "btn-light"}`}
                                title="Toggle advanced options"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    {showAdvanced && (
                        <div className="px-6 py-3 bg-gray-50 border-t border-b border-gray-200 space-y-2">
                            {groupBy === 'month' && groupedData.some(d => d.hideFromGraph) && (
                                <div>
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={showCurrentMonth}
                                            onChange={(e) => setShowCurrentMonth(e.target.checked)}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span>Show current month on graph (actual + projection)</span>
                                    </label>
                                </div>
                            )}
                            {trendInfo && groupBy === 'month' && (
                                <div className="text-sm text-gray-600">
                                    <span className="font-semibold">Trend Analysis:</span>
                                    <span className="ml-3">
                                        Model: {trendInfo.type === 'exponential' ? 'Exponential' : 'Linear'}
                                    </span>
                                    <span className="ml-4">
                                        Linear R²: {(trendInfo.linearR2 || 0).toFixed(3)}
                                    </span>
                                    <span className="ml-4">
                                        Exponential R²: {(trendInfo.exponentialR2 || 0).toFixed(3)}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="card-body flex-1 flex flex-col min-h-0">
                        <div className="h-[300px] lg:h-[calc(100vh-320px)]">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={(trendLineData || groupedData).filter(d => showCurrentMonth ? true : !d.hideFromGraph)}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="date"
                                        interval={groupBy === "month" ? 0 : "preserveStartEnd"}
                                        angle={groupBy === "month" && groupedData.length > 12 ? -45 : 0}
                                        textAnchor={groupBy === "month" && groupedData.length > 12 ? "end" : "middle"}
                                        height={groupBy === "month" && groupedData.length > 12 ? 60 : undefined}
                                    />
                                    <YAxis allowDecimals={false} />
                                    <Tooltip
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length > 0) {
                                                const data = payload[0].payload;
                                                const trendValue = payload.find((p: any) => p.dataKey === 'trend');
                                                return (
                                                    <div className="bg-white p-2 border border-gray-300 rounded shadow">
                                                        <p className="font-semibold">{data.date}</p>
                                                        {data.isProjected ? (
                                                            <>
                                                                <p className="text-amber-600">Current: {data.actualCount}</p>
                                                                <p className="text-gray-500">Projected: {data.count}</p>
                                                            </>
                                                        ) : (
                                                            <p>Count: {data.count}</p>
                                                        )}
                                                        {trendValue && trendValue.value !== undefined && (
                                                            <p className="text-gray-400">Trend: {Math.round(Number(trendValue.value))}</p>
                                                        )}
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                    {trendLineData && (
                                        <Line
                                            type="natural"
                                            dataKey="trend"
                                            stroke="#9ca3af"
                                            strokeWidth={1.5}
                                            strokeDasharray="5 5"
                                            dot={false}
                                            activeDot={false}
                                            isAnimationActive={false}
                                        />
                                    )}
                                    <Line
                                        type="monotone"
                                        dataKey={(d: any) => (d.hideFromGraph && showCurrentMonth) ? null : d.count}
                                        stroke="#3b82f6"
                                        strokeWidth={2}
                                        dot={(props: any) => {
                                            const { cx, cy, payload, index } = props;
                                            if (payload.isProjected && !payload.hideFromGraph) {
                                                return <circle key={`dot-projected-${index}`} cx={cx} cy={cy} r={4} fill="#9ca3af" />;
                                            }
                                            if (!payload.hideFromGraph && !payload.isProjected) {
                                                return <circle key={`dot-${index}`} cx={cx} cy={cy} r={3} fill="#3b82f6" />;
                                            }
                                            return null;
                                        }}
                                        connectNulls={false}
                                        isAnimationActive={false}
                                    />
                                    {showCurrentMonth && (
                                        <>
                                            <Line
                                                type="monotone"
                                                dataKey="count"
                                                stroke="transparent"
                                                strokeWidth={0}
                                                dot={(props: any) => {
                                                    const { cx, cy, payload, index } = props;
                                                    if (payload.hideFromGraph && payload.isProjected) {
                                                        return <circle key={`dot-current-projected-${index}`} cx={cx} cy={cy} r={4} fill="#9ca3af" />;
                                                    }
                                                    return null;
                                                }}
                                                connectNulls={false}
                                                isAnimationActive={false}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="actualCount"
                                                stroke="transparent"
                                                strokeWidth={0}
                                                dot={(props: any) => {
                                                    const { cx, cy, payload, index } = props;
                                                    if (payload.hideFromGraph && payload.actualCount) {
                                                        return <circle key={`dot-current-actual-${index}`} cx={cx} cy={cy} r={5} fill="#fbbf24" stroke="#f59e0b" strokeWidth={1} />;
                                                    }
                                                    return null;
                                                }}
                                                connectNulls={false}
                                                isAnimationActive={false}
                                            />
                                        </>
                                    )}
                                    <Line
                                        type="monotone"
                                        dataKey="actualCount"
                                        stroke="transparent"
                                        strokeWidth={0}
                                        dot={(props: any) => {
                                            const { cx, cy, payload, index } = props;
                                            if (payload.actualCount && !payload.hideFromGraph) {
                                                return <circle key={`dot-actual-${index}`} cx={cx} cy={cy} r={5} fill="#fbbf24" stroke="#f59e0b" strokeWidth={1} />;
                                            }
                                            return null;
                                        }}
                                        connectNulls={false}
                                        isAnimationActive={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* CMGR Detail Modal */}
        {showCmgrModal && trendInfo?.cmgrDetails && (() => {
            const d = trendInfo.cmgrDetails;
            const cmgrPct = (d.cmgr * 100).toFixed(2);
            const ratio = (d.lastValue / d.firstValue).toFixed(4);
            const exponent = `1/${d.periods}`;
            const rootResult = Math.pow(d.lastValue / d.firstValue, 1 / d.periods).toFixed(6);

            // Find current month data for the toggle
            const currentMonthEntry = groupedData.find((g: any) => g.isProjected || g.hideFromGraph);
            const currentMonthActual = (currentMonthEntry as any)?.actualCount || (currentMonthEntry as any)?.count;
            const currentMonthProjected = (currentMonthEntry as any)?.count;
            const currentMonthDate = currentMonthEntry?.date;
            const hasCurrentMonth = !!currentMonthEntry && !!currentMonthActual;

            return (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCmgrModal(false)}>
                    <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
                        <div className="p-5 pb-8">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold text-gray-900">CMGR Calculation Details</h3>
                                <button onClick={() => setShowCmgrModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
                            </div>

                            {/* Result */}
                            <div className={`${d.cmgr >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-lg p-4 mb-4 text-center`}>
                                <div className={`text-3xl font-bold ${d.cmgr >= 0 ? 'text-green-700' : 'text-red-700'}`}>{cmgrPct}%</div>
                                <div className={`text-sm ${d.cmgr >= 0 ? 'text-green-600' : 'text-red-600'}`}>Compound Monthly Growth Rate</div>
                            </div>

                            {/* Include current month toggle */}
                            {hasCurrentMonth && (
                                <label className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 cursor-pointer text-sm">
                                    <input
                                        type="checkbox"
                                        checked={cmgrIncludeCurrent}
                                        onChange={(e) => {
                                            const val = e.target.checked;
                                            setCmgrIncludeCurrent(val);
                                            localStorage.setItem('signaturesGraph-cmgrIncludeCurrent', String(val));
                                        }}
                                        className="rounded"
                                    />
                                    <div>
                                        <span className="font-semibold text-blue-800">Include current month</span>
                                        <span className="text-blue-600 ml-1">({currentMonthDate}: {currentMonthProjected?.toLocaleString()} projected from {currentMonthActual?.toLocaleString()} actual)</span>
                                    </div>
                                </label>
                            )}

                            {/* Method explanation */}
                            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                                <div className="font-semibold text-gray-700 mb-1">
                                    Method: {d.method === 'regression' ? 'Full Range' : 'Trailing Window'}
                                </div>
                                <p className="text-gray-600">
                                    {d.method === 'regression'
                                        ? `With ${d.dataPoints} months of data, CMGR uses the full date range (after filtering early ramp-up months). This provides a stable, long-term growth rate.`
                                        : `With ${d.dataPoints} months of data (<12), CMGR uses a trailing ${d.windowSize}-month window to focus on the most recent growth trajectory.`
                                    }
                                </p>
                            </div>

                            {/* Formula */}
                            <div className="mb-4">
                                <div className="font-semibold text-gray-700 text-sm mb-2">Formula</div>
                                <div className="bg-gray-900 text-green-400 rounded-lg p-3 font-mono text-sm">
                                    CMGR = (Ending / Beginning)<sup>1/n</sup> - 1
                                </div>
                            </div>

                            {/* Step by step */}
                            <div className="mb-4">
                                <div className="font-semibold text-gray-700 text-sm mb-2">Step-by-Step</div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between py-1.5 px-3 bg-gray-50 rounded">
                                        <span className="text-gray-600">Beginning Value ({d.firstDate})</span>
                                        <span className="font-semibold">{d.firstValue.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between py-1.5 px-3 bg-gray-50 rounded">
                                        <span className="text-gray-600">Ending Value ({d.lastDate})</span>
                                        <span className="font-semibold">{d.lastValue.toLocaleString()}</span>
                                    </div>
                                    <div className="flex justify-between py-1.5 px-3 bg-gray-50 rounded">
                                        <span className="text-gray-600">Months Spanned (n)</span>
                                        <span className="font-semibold">{d.periods}</span>
                                    </div>
                                    <div className="border-t pt-2 mt-2 px-3 space-y-1 text-gray-600">
                                        <div>1. Ending / Beginning = {d.lastValue.toLocaleString()} / {d.firstValue.toLocaleString()} = {ratio}</div>
                                        <div>2. Raise to power {exponent} = {ratio}<sup>{exponent}</sup> = {rootResult}</div>
                                        <div>3. Subtract 1 = {rootResult} - 1 = <span className="font-semibold text-green-700">{cmgrPct}%</span></div>
                                    </div>
                                </div>
                            </div>

                            {/* Window info */}
                            {d.dataPoints !== d.windowSize && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
                                    {d.dataPoints} total months of data available. {d.dataPoints - d.windowSize} early months were filtered (ramp-up period below 20% of median).
                                </div>
                            )}

                            {/* Projection Methodology */}
                            {projectedMonthlyValue && valueMetrics && (
                                <div className="mb-4">
                                    <div className="font-semibold text-gray-700 text-sm mb-2">Projection Methodology</div>
                                    <p className="text-xs text-gray-500 mb-3">How we derive projected leads, policies, and revenue from the CMGR above.</p>

                                    <div className="space-y-2 text-sm">
                                        {/* Current baseline */}
                                        <div className="bg-gray-50 rounded-lg p-3">
                                            <div className="font-semibold text-gray-700 mb-1">1. Current Monthly Baseline</div>
                                            {(() => {
                                                const baselineValue = cmgrIncludeCurrent
                                                    ? ((groupedData.find((g: any) => g.isProjected || g.hideFromGraph) as any)?.count || 0)
                                                    : (groupedData.filter(g => !g.hideFromGraph && !g.isProjected).slice(-1)[0]?.count || 0);
                                                const baselineLabel = cmgrIncludeCurrent
                                                    ? `Current month projected (${currentMonthDate})`
                                                    : 'Last full month signatures';
                                                return (
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">{baselineLabel}</span>
                                                        <span className="font-semibold">{baselineValue.toLocaleString()}</span>
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        {/* 12-month projection */}
                                        <div className="bg-gray-50 rounded-lg p-3">
                                            <div className="font-semibold text-gray-700 mb-1">2. 12-Month Growth Projection</div>
                                            <div className="text-xs text-gray-500 mb-1">
                                                {(() => {
                                                    const baselineValue = cmgrIncludeCurrent
                                                        ? ((groupedData.find((g: any) => g.isProjected || g.hideFromGraph) as any)?.count || 0)
                                                        : (groupedData.filter(g => !g.hideFromGraph && !g.isProjected).slice(-1)[0]?.count || 0);
                                                    return baselineValue.toLocaleString();
                                                })()} &times; (1 + {(d.cmgr * 100).toFixed(2)}%)<sup>12</sup> = <span className="font-semibold">{projectedMonthlyValue.projectedSignatures.toLocaleString()}</span> signatures/mo
                                            </div>
                                        </div>

                                        {/* Lead classification */}
                                        <div className="bg-gray-50 rounded-lg p-3">
                                            <div className="font-semibold text-gray-700 mb-1">3. Lead Classification</div>
                                            <div className="text-xs text-gray-500 mb-2">Based on actual preneed rate from your data: {totalSignatures > 0 ? ((preneedCount / totalSignatures) * 100).toFixed(1) : 0}%</div>
                                            <div className="flex justify-between py-1">
                                                <span className="text-gray-600">High-intent (preneed) leads</span>
                                                <span className="font-semibold">{projectedMonthlyValue.highIntentConversions > 0 ? Math.round(projectedMonthlyValue.projectedSignatures * (preneedCount / totalSignatures)).toLocaleString() : '0'}/mo</span>
                                            </div>
                                            <div className="flex justify-between py-1">
                                                <span className="text-gray-600">General leads</span>
                                                <span className="font-semibold">{(projectedMonthlyValue.projectedSignatures - Math.round(projectedMonthlyValue.projectedSignatures * (preneedCount / totalSignatures))).toLocaleString()}/mo</span>
                                            </div>
                                        </div>

                                        {/* Conversion rates */}
                                        <div className="bg-gray-50 rounded-lg p-3">
                                            <div className="font-semibold text-gray-700 mb-1">4. Policy Conversion</div>
                                            <div className="flex justify-between py-1">
                                                <span className="text-gray-600">High-intent @ 35% close rate</span>
                                                <span className="font-semibold">{projectedMonthlyValue.highIntentConversions.toLocaleString()} policies/mo</span>
                                            </div>
                                            <div className="flex justify-between py-1">
                                                <span className="text-gray-600">General @ 2% conversion rate</span>
                                                <span className="font-semibold">{projectedMonthlyValue.generalConversions.toLocaleString()} policies/mo</span>
                                            </div>
                                            <div className="flex justify-between py-1 border-t border-gray-200 mt-1 pt-1">
                                                <span className="text-gray-700 font-semibold">Total policies</span>
                                                <span className="font-semibold">{projectedMonthlyValue.conversions.toLocaleString()}/mo</span>
                                            </div>
                                        </div>

                                        {/* Revenue calculation */}
                                        <div className="bg-gray-50 rounded-lg p-3">
                                            <div className="font-semibold text-gray-700 mb-1">5. Revenue &amp; Profit</div>
                                            <div className="flex justify-between py-1">
                                                <span className="text-gray-600">{projectedMonthlyValue.conversions.toLocaleString()} policies &times; ${policyValue.toLocaleString()} avg value</span>
                                                <span className="font-semibold">${projectedMonthlyValue.monthlyRevenue.toLocaleString()}/mo</span>
                                            </div>
                                            <div className="flex justify-between py-1">
                                                <span className="text-gray-600">@ {insuranceMargin}% margin</span>
                                                <span className="font-semibold">${Math.round(projectedMonthlyValue.monthlyRevenue * (insuranceMargin / 100)).toLocaleString()}/mo</span>
                                            </div>
                                            <div className="flex justify-between py-1 border-t border-gray-200 mt-1 pt-1">
                                                <span className="text-gray-700 font-semibold">Annual profit</span>
                                                <span className="font-bold text-green-700">${Math.round(projectedMonthlyValue.monthlyRevenue * 12 * (insuranceMargin / 100)).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* External link */}
                            <div className="border-t pt-3 mt-2">
                                <a
                                    href="https://www.wallstreetprep.com/knowledge/compound-monthly-growth-rate-cmgr/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                    Learn more about CMGR on Wall Street Prep &rarr;
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            );
        })()}
    </>);
}
